/** English UI text. `bg.ts` must mirror every key (a test checks). Sentence case, no "!" */
export const en = {
  common: {
    actions: 'Actions',
    cancel: 'Cancel',
    close: 'Close',
    dismiss: 'Dismiss',
    loading: 'Loading',
    more: 'More actions',
    save: 'Save',
  },
  form: {
    required: 'Required.',
    notANumber: 'Enter a number.',
  },
  errors: {
    generic: 'Something went wrong. Try again.',
    appFailed: 'Botimir stopped working',
    appFailedBody: 'Reload the page to continue. Nothing you saved is lost.',
    reload: 'Reload',
    pageFailed: 'This page failed to load',
    pageFailedBody: 'Try again. If it keeps failing, reload the app.',
    tryAgain: 'Try again',
  },
  serverErrors: {
    price_not_positive: 'Share price must be above zero.',
    sum_not_positive: 'Initial sum must be above zero.',
    sum_too_small: 'The initial sum doesn’t buy a single share.',
    levels_range: 'Levels must be between 1 and 6.',
    step_range: 'Step must be between 1% and 50%.',
    multiplier_range: 'Multiplier must be between 1 and 4.',
    target_range: 'Target must be between 0.1% and 100%.',
    tick_invalid: 'Tick size must be above 0 and at most 1.',
    not_positive: 'Must be above zero.',
    whole_shares: 'Use whole shares.',
    share_decimals: 'Use at most 4 decimals.',
    time_zone_missing: 'Include a time zone.',
    time_future: 'The execution time can’t be in the future.',
    time_before_open: 'The execution time can’t be before the position opened.',
    isin_invalid: 'Enter a 12-character ISIN, like IE00B5BMR087.',
    symbol_invalid: 'Enter a market symbol, like SXR8.DE.',
    signal_invalid: 'Enter a market symbol, like ^GSPC.',
    name_required: 'Enter the instrument’s name.',
    isin_taken: 'This ISIN is already on the watchlist.',
    symbol_taken: 'This symbol is already on the watchlist.',
    instrument_not_found: 'Instrument not found.',
    instrument_in_use: 'This instrument has positions. Deactivate it instead.',
    instrument_inactive: 'This instrument is inactive.',
    position_not_found: 'Position not found.',
    position_not_open: 'This position is no longer open.',
    level_not_found: 'Level not found.',
    level_closed: 'This level is already filled or skipped.',
    fill_not_found: 'Fill not found.',
    fill_not_removable: 'Only ladder buys can be removed. Cancel the position instead.',
    alert_not_found: 'Alert not found.',
    alert_not_open: 'Only open alerts can be dismissed.',
    notes_too_long: 'Notes are limited to 5,000 characters.',
    email_missing: 'Add a notification email first.',
    bad_credentials: 'Email or password is incorrect.',
    rate_limited: 'Too many attempts. Try again later.',
    sign_in_required: 'Sign in to continue.',
    csrf: 'Your session changed. Reload the page.',
    invalid_value: 'Enter a valid value.',
    not_found: 'Not found.',
    server: 'Something went wrong. Try again.',
    network: 'Network error. Check your connection.',
    symbol_unknown: 'No market data for this symbol. Check it on Yahoo Finance.',
    chart_duplicate: 'A symbol is on the dashboard twice.',
    charts_too_many: 'The dashboard holds up to 24 charts.',
    market_unavailable: 'Market data is unavailable right now. Try again in a minute.',
  },
  session: {
    signOut: 'Sign out',
  },
  home: {
    eyebrow: 'S&P 500 · Nasdaq-100 · Xetra',
    headline: 'A plan for every dip.',
    headline2: 'Sell when it pays.',
    lead: 'Botimir plans a scale-in ladder for European S&P 500 and Nasdaq-100 ETFs, watches Xetra every five minutes, and emails you when it’s time to buy a level or sell everything at your target.',
    example:
      'Example: €1,000 to start, levels 10% apart, twice the shares at each level, 5% target.',
    stepsTitle: 'How it works',
    plan: 'Plan the ladder',
    planBody:
      'Enter the price and the sum you start with. See every level, its size and the capital you’d need if all of them fill.',
    watch: 'Let it watch',
    watchBody:
      'Quotes every five minutes while Xetra is open, with the market regime for context. No order is ever placed for you.',
    act: 'Act on the email',
    actBody:
      'A message when a level or the target is reached, with a limit price and the best time to trade. Record what you did in seconds.',
    links: 'Research',
    copyright: 'Botimir © {{year}}',
  },
  signIn: {
    title: 'Sign in',
    body: '',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    missing: 'Enter your email and password.',
  },
  nav: {
    label: 'Main',
    positions: 'Positions',
    alerts: 'Alerts',
    stats: 'Stats',
    charts: 'Charts',
    watchlist: 'Watchlist',
    settings: 'Settings',
  },
  shell: {
    toggleSidebar: 'Toggle sidebar',
    toggleInspector: 'Toggle details panel',
    breadcrumb: 'Breadcrumb',
    search: 'Search',
    notifications: 'Notifications',
    details: 'Position details',
  },
  market: {
    open: '{{exchange}} open',
    closed: '{{exchange}} closed',
    closesAt: 'Closes at {{time}}',
    opensAt: 'Opens at {{time}}',
  },
  marketStrip: {
    label: 'Market',
    fromHigh: '{{drawdown}} from high',
  },
  regime: {
    uptrend: 'Uptrend',
    downtrend: 'Downtrend',
    uptrendLower: 'uptrend',
    downtrendLower: 'downtrend',
    trendHint:
      'Close against the 200-day average, confirmed by 3 closes. Days in the current trend.',
    volatilityHint: '20-day realised volatility, annualised.',
    volLower: {
      calm: 'calm',
      normal: 'normal',
      stressed: 'stressed',
    },
    vol: {
      calm: 'Calm',
      normal: 'Normal',
      stressed: 'Stressed',
    },
  },
  analogs: {
    loading: 'Looking at past ladders like this one…',
    sentence:
      'In conditions like today ({{conditions}}): {{level2}} of past ladders reached level 2, {{all}} needed the full amount. Median {{median}} trading days to target; worst {{worst}}.',
    anyConditions: 'all conditions',
    caveat:
      'Based on {{entries}} overlapping historical entries over {{years}} years; few independent episodes.',
  },
  positions: {
    new: 'New position',
    emptyTitle: 'No open positions',
    emptyBody: 'Open a position when you buy. Botimir plans the ladder and emails you when to act.',
    keyboardHint: 'Move, open and record with',
  },
  positionCard: {
    label: '{{symbol}} position',
    invested: 'Invested {{amount}}',
    atTarget: 'At target',
    buyDue: 'Buy due',
    toLevel: '{{distance}} to L{{n}}',
    toTarget: '{{distance}} to target',
    summary:
      '{{shares}} shares · average {{average}} · target {{target}} · {{remaining}} still needed for the remaining levels',
  },
  ladderTrack: {
    label: 'Ladder: price {{current}}, target {{target}}, {{filled}} of {{total}} levels bought',
  },
  ladder: {
    level: 'Level',
    levelN: 'Level {{n}}',
    entry: 'Entry',
    entryShort: 'Entry',
    price: 'Price',
    trigger: 'Trigger price',
    shares: 'Shares',
    cost: 'Cost',
    cumulative: 'Cumulative',
    targetIfFilled: 'Target if filled',
    status: 'Status',
    fill: 'Fill',
    target: 'Target',
    buy: 'Buy',
    sell: 'Sell',
    statuses: {
      pending: 'Pending',
      triggered: 'Buy now',
      filled: 'Filled',
      skipped: 'Skipped',
    },
  },
  position: {
    status: {
      open: 'Open',
      target_reached: 'Sell all',
      closed: 'Closed',
      cancelled: 'Cancelled',
      buy_one: 'Buy {{count}} level',
      buy_other: 'Buy {{count}} levels',
    },
    invested: 'Invested',
    marketValue: 'Market value',
    proceeds: 'Proceeds',
    pl: 'P/L',
    toTarget: 'Target',
    shares: 'Shares',
    sharesAt: '{{shares}} shares at {{average}}',
    lastPrice: '{{price}} · {{time}}',
    noQuote: 'No quote yet',
    targetReached: 'Reached',
    away: '{{distance}} away',
    targetOf: '{{target}} on the total',
    newAverage: 'New average',
    newTarget: 'New target',
    ladder: 'Ladder',
    recordFill: 'Record fill',
    recordFillShort: 'Record fill',
    recordFillLevel: 'Record fill for level {{n}}',
    editTarget: 'Edit target',
    close: 'Close position',
    cancel: 'Cancel position',
    cancelTitle: 'Cancel this position?',
    cancelBody:
      'Use this for a position opened by mistake. It is left out of stats and stops all alerts.',
    skip: 'Skip level',
    skipLevel: 'Skip level {{n}}',
    skipTitle: 'Skip level {{n}}?',
    skipBody: 'Botimir stops alerting for this level. The other levels stay as planned.',
    undoFill: 'Undo fill',
    undoFillLevel: 'Undo the fill of level {{n}}',
    undoTitle: 'Undo the fill of level {{n}}?',
    undoBody: 'The level goes back to waiting and the average and target are recalculated.',
    priceChart: 'Price',
    priceChartLabel:
      '{{symbol}} daily closes for the last six months with ladder levels and the target',
    sixMonths: 'Last 6 months',
    regime: 'Market regime',
    atEntry: 'At entry',
    now: 'Now',
    age: 'Age',
    sessions_one: '{{count}} session',
    sessions_other: '{{count}} sessions',
    ageLine: 'Day {{day}} · median {{median}} · p90 {{p90}}',
    regimeCaveat:
      'Regime describes the market; it doesn’t predict it. Medians come from past ladders with your settings.',
    alerts: 'Alerts',
    notes: 'Notes',
    notesPlaceholder: 'Why you opened it, what you expect…',
    notFound: 'Position not found',
  },
  openPosition: {
    title: 'New position',
    description: 'Record the first buy. Botimir plans the ladder from it.',
    instrument: 'Instrument',
    pickInstrument: 'Pick an instrument',
    price: 'Share price',
    priceHint: 'The price you bought at. Prefilled with the latest quote.',
    initialSum: 'Initial sum',
    target: 'Target profit',
    ladderSettings: 'Ladder settings',
    ladderSummary: '{{levels}} levels · {{step}}% apart · ×{{multiplier}} shares',
    levels: 'Levels',
    step: 'Step',
    stepHint: 'Each level is this much below the previous one.',
    multiplier: 'Multiplier',
    multiplierHint: 'Each level buys this many times the shares of the previous one.',
    plan: 'Plan',
    planEmpty: 'Enter a price and an initial sum to see the ladder.',
    capitalNeeded: 'Capital needed if all levels fill',
    leftover: '{{shares}} shares; {{leftover}} of the initial sum stays uninvested.',
    submit: 'Open position',
    opened: '{{symbol}} position opened.',
  },
  recordFill: {
    title: 'Record fill · {{symbol}}',
    description: 'What you actually bought. The next levels keep their planned prices.',
    level: 'Level',
    price: 'Price',
    shares: 'Shares',
    executedAt: 'Executed at',
    submit: 'Record fill',
    recorded: 'Level {{n}} recorded.',
  },
  closePosition: {
    title: 'Close position · {{symbol}}',
    description: 'Record the sale of every share.',
    price: 'Sale price',
    shares: 'Shares',
    executedAt: 'Executed at',
    proceeds: 'Proceeds',
    result: 'Result',
    target: 'Target price {{price}}',
    submit: 'Close position',
    closed: '{{symbol}} position closed.',
  },
  editTarget: {
    title: 'Edit target',
    description: 'The profit to make on the total invested.',
    saved: 'Target updated.',
  },
  alerts: {
    filter: 'Show',
    open: 'Open',
    all: 'All',
    emptyOpen: 'Nothing to do',
    emptyAll: 'No alerts yet',
    emptyBody: 'Botimir records an alert and emails you when a level or the target is reached.',
    levelHit: 'Buy level {{n}} of {{symbol}} at or below {{price}}',
    targetHit: 'Sell all {{symbol}} at or above {{price}}',
    marketOpen: 'Xetra opened with no position open',
    regimeChange: '{{symbol}}: trend turned {{trend}}',
    positionLink: '{{symbol}} #{{id}}',
    recordFill: 'Record fill',
    recordSale: 'Record sale',
    dismiss: 'Dismiss',
    statuses: {
      open: 'Open',
      done: 'Done',
      dismissed: 'Dismissed',
      info: 'Info',
    },
  },
  stats: {
    summary: 'Summary',
    realized: 'Realised P/L',
    maxDeployed: 'Peak capital {{amount}}',
    xirr: 'Annual return',
    xirrHint: 'Money-weighted',
    medianDays: 'Median holding',
    calendarDays: 'Calendar days',
    closed: 'Closed positions',
    vsBuyHold: 'vs buy and hold',
    benchmark: 'Buy and hold {{rate}}',
    noBenchmark: 'Not enough price history',
    cumulativePl: 'Cumulative realised P/L',
    cumulativePlLabel: 'Cumulative realised profit and loss over time',
    deployed: 'Capital deployed',
    deployedLabel: 'Capital tied up in positions over time',
    notEnoughPoints: 'Not enough data for a chart yet.',
    closedPositions: 'Closed positions',
    annualisedHidden: 'Shown for holds of 30 days or more.',
    emptyTitle: 'No closed positions yet',
    emptyBody: 'Stats appear once you close a position.',
    cols: {
      opened: 'Opened',
      closed: 'Closed',
      symbol: 'Symbol',
      deepest: 'Deepest',
      shares: 'Shares',
      invested: 'Invested',
      average: 'Average',
      closePrice: 'Close price',
      proceeds: 'Proceeds',
      pl: 'P/L',
      plPct: 'P/L %',
      days: 'Days held',
      annualised: 'Annualised',
      regime: 'Regime at entry',
    },
  },
  charts: {
    edit: 'Edit symbols',
    emptyTitle: 'No charts yet',
    emptyBody:
      'Add any Yahoo Finance symbol: indexes like ^GSPC, ETFs like SXR8.DE, FX like EURUSD=X.',
    periods: {
      '1d': '1D',
      '5d': '5D',
      '1mo': '1M',
      '6mo': '6M',
      ytd: 'YTD',
      '1y': '1Y',
      '5y': '5Y',
      max: 'Max',
    },
    periodFor: '{{symbol}} period',
    chartLabel: '{{symbol}} price chart, {{period}}',
    tableCaption: '{{symbol}} prices, {{period}}',
    dayChange: '{{value}} today',
    periodChange: '{{value}} over {{period}}',
    reset: 'Show the whole period',
    showLine: 'Show as line',
    showCandles: 'Show as candles',
    date: 'Date',
    close: 'Close',
    retry: 'Retry',
    viewAsTable: 'View as table',
    viewAsChart: 'View as chart',
    legend: { open: 'O', high: 'H', low: 'L', close: 'C', volume: 'V' },
    table: {
      time: 'Time',
      open: 'Open',
      high: 'High',
      low: 'Low',
      close: 'Close',
      volume: 'Volume',
    },
    footnote: 'Prices from Yahoo Finance, delayed about 15 minutes. Times in {{zone}}.',
    attribution: 'Charts by',
    attributionLink: 'TradingView Lightweight Charts™',
    editTitle: 'Chart symbols',
    editDescription:
      'Any Yahoo Finance symbol, separate from the watchlist. Drag to reorder; the first one comes first on the dashboard.',
    symbol: 'Symbol',
    symbolPlaceholder: 'e.g. ^GDAXI',
    add: 'Add',
    count: '{{count}} of {{max}} charts',
    full: 'The dashboard holds up to {{max}} charts.',
    duplicate: '{{symbol}} is already on the dashboard.',
    emptyList: 'No symbols. Add one above.',
    listLabel: 'Charts on the dashboard',
    move: 'Move {{symbol}}',
    remove: 'Remove {{symbol}}',
    dragInstructions:
      'To reorder, press Space, move with the arrow keys, then press Space to drop or Escape to cancel.',
    pickedUp: 'Picked up {{symbol}}, position {{position}} of {{total}}.',
    movedTo: '{{symbol}} moved to position {{position}} of {{total}}.',
    dropped: '{{symbol}} dropped at position {{position}} of {{total}}.',
    cancelled: 'Moving {{symbol}} was cancelled.',
  },
  watchlist: {
    add: 'Add instrument',
    addTitle: 'Add instrument',
    addDescription: 'A European-listed ETF traded in euros on Xetra.',
    isin: 'ISIN',
    isinHint: 'For example IE00B5BMR087.',
    symbol: 'Symbol',
    symbolHint: 'The Yahoo symbol, for example SXR8.DE.',
    name: 'Name',
    signal: 'Signal symbol',
    signalHint:
      'Regime and history come from this symbol, for example ^GSPC. Defaults to the instrument.',
    tick: 'Tick size',
    fractional: 'Fractional shares',
    fractionalHint: 'Your broker lets you buy parts of a share.',
    last: 'Last',
    day: 'Day',
    drawdown: 'From high',
    trend: 'Trend',
    volatility: 'Volatility',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    remove: 'Remove',
    removeTitle: 'Remove {{symbol}}?',
    removeBody: 'Instruments with positions can’t be removed; deactivate them instead.',
    actionsFor: 'Actions for {{symbol}}',
    emptyTitle: 'Your watchlist is empty',
    emptyBody: 'Add the S&P 500 and Nasdaq-100 ETFs you trade.',
    regimeNote: 'Regime describes the market; it doesn’t predict it.',
  },
  settings: {
    notifications: 'Notifications',
    email: 'Email',
    emailHint: 'Where alerts go.',
    emailInvalid: 'Enter a valid email address.',
    sendTest: 'Send test email',
    testSent: 'Test email queued for {{email}}.',
    marketOpenEmail: 'Market-open email',
    marketOpenHint: 'When Xetra opens and no position is open.',
    regimeEmails: 'Regime emails',
    regimeHint: 'When a watched index’s trend turns.',
    reminderDays: 'Reminders after',
    reminderHint: 'If an alert isn’t acted on. At most two reminders.',
    sessions_one: '{{count}} session',
    sessions_other: '{{count}} sessions',
    defaults: 'Defaults for new positions',
    appearance: 'Appearance',
    languageHint: 'Emails use it too.',
    region: 'Region',
    timezone: 'Time zone',
    timezoneHint: 'For dates in emails and stats.',
    system: 'System',
    dataFreshness: 'Market data',
    lastQuote: 'Last quote {{time}}',
    noQuotes: 'No quotes yet',
    emailDelivery: 'Email delivery',
    outbox: '{{pending}} waiting · {{failed}} failed',
    neverRan: 'Never ran',
    jobStatus: {
      ok: 'OK',
      failed: 'Failed',
      running: 'Running',
    },
    saved: 'Settings saved.',
  },
  preferences: {
    menu: 'Preferences',
    theme: 'Theme',
    language: 'Language',
    themes: {
      system: 'System',
      dark: 'Dark',
      light: 'Light',
    },
  },
  palette: {
    title: 'Command palette',
    placeholder: 'Type a command or search…',
    empty: 'No results.',
    actions: 'Actions',
    navigation: 'Go to',
    positions: 'Positions',
    preferences: 'Preferences',
    newPosition: 'New position',
    recordFill: 'Record fill · {{symbol}} level {{n}}',
    addInstrument: 'Add instrument',
    editCharts: 'Edit chart symbols',
    goTo: 'Go to {{page}}',
    openPosition: 'Open {{symbol}} #{{id}}',
    theme: 'Theme: {{theme}}',
    language: 'Language: {{language}}',
    shortcuts: 'Keyboard shortcuts',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    groups: {
      general: 'General',
      navigation: 'Navigation',
      positions: 'Positions',
    },
    items: {
      palette: 'Command palette',
      help: 'Keyboard shortcuts',
      newPosition: 'New position',
      goPositions: 'Go to positions',
      goAlerts: 'Go to alerts',
      goStats: 'Go to stats',
      goCharts: 'Go to charts',
      goWatchlist: 'Go to watchlist',
      goSettings: 'Go to settings',
      next: 'Next position',
      previous: 'Previous position',
      open: 'Open position',
      recordFill: 'Record fill',
      toggleSidebar: 'Toggle sidebar',
      toggleInspector: 'Toggle details panel',
      submit: 'Submit dialog',
      close: 'Close or cancel',
    },
  },
  notFound: {
    title: 'Page not found',
    body: 'The link may be old. Go back to your positions.',
    back: 'Go to positions',
  },
} as const;

type DeepString<T> = { [K in keyof T]: T[K] extends string ? string : DeepString<T[K]> };
export type Resources = DeepString<typeof en>;
